import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Math.abs(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// payeeType: "contractor" | "mover". items: adjustments for this payee+week. jobs: [{job_id,date}] for job scope.
export const PayoutAdjustments = ({ payeeType, payeeName, contractorId, weekStart, jobs = [], items = [], onChange, idPrefix }) => {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("addon");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [scope, setScope] = useState("week");
  const [jobId, setJobId] = useState("");
  const [saving, setSaving] = useState(false);

  const label = payeeType === "contractor" ? "contractor payout" : "driver pay";

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    if (scope === "job" && !jobId) { toast.error("Pick a job"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/payout-adjustments`, {
        payee_type: payeeType,
        payee_name: payeeName,
        contractor_id: contractorId,
        scope,
        week_start: weekStart,
        job_id: scope === "job" ? jobId : null,
        kind,
        amount: amt,
        reason,
      });
      toast.success(kind === "addon" ? "Add-on added" : "Deduction added");
      setAmount(""); setReason(""); setJobId(""); setScope("week"); setKind("addon"); setOpen(false);
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/payout-adjustments/${id}`);
      toast.success("Removed");
      onChange && onChange();
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-dashed border-slate-300 bg-slate-50/70 p-2">
      {items.length > 0 && (
        <div className="space-y-1">
          {items.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`adj-item-${a.id}`}>
              <span className="flex min-w-0 items-center gap-1.5 truncate">
                <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${a.kind === "addon" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                  {a.kind === "addon" ? "Add-on" : "Deduct"}
                </span>
                <span className="truncate text-slate-600">{a.reason || (a.scope === "job" ? a.job_id : "This week")}{a.scope === "job" && a.reason ? ` · ${a.job_id}` : ""}</span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                <span className={`font-bold ${a.kind === "addon" ? "text-green-700" : "text-red-600"}`}>{a.kind === "addon" ? "+" : "−"}{money(a.amount)}</span>
                <button type="button" onClick={() => remove(a.id)} className="rounded p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`adj-remove-${a.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-1 flex w-full items-center justify-center gap-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-800"
          data-testid={`${idPrefix}-add-btn`}
        >
          <Plus className="h-3 w-3" /> Add-on / Deduction
        </button>
      ) : (
        <div className="mt-1 space-y-2 rounded-lg bg-white p-2.5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Adjust {label}</p>
            <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid={`${idPrefix}-close`}><X className="h-3.5 w-3.5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {[["addon", "Add-on"], ["deduction", "Deduction"]].map(([k, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={`rounded-lg px-2 py-1.5 text-[11px] font-bold transition-colors ${kind === k ? (k === "addon" ? "bg-green-600 text-white" : "bg-red-600 text-white") : "bg-slate-100 text-slate-500"}`}
                data-testid={`${idPrefix}-kind-${k}`}
              >
                {l}
              </button>
            ))}
          </div>
          <input
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Amount ($)"
            className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-slate-500"
            data-testid={`${idPrefix}-amount`}
          />
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (e.g. Fuel bonus, Damage)"
            className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-slate-500"
            data-testid={`${idPrefix}-reason`}
          />
          <div className="grid grid-cols-2 gap-1">
            {[["week", "This week"], ["job", "A job"]].map(([s, l]) => (
              <button
                key={s}
                type="button"
                onClick={() => setScope(s)}
                className={`rounded-lg px-2 py-1.5 text-[11px] font-bold transition-colors ${scope === s ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500"}`}
                data-testid={`${idPrefix}-scope-${s}`}
              >
                {l}
              </button>
            ))}
          </div>
          {scope === "job" && (
            <select
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-slate-500"
              data-testid={`${idPrefix}-job-select`}
            >
              <option value="">Pick a job…</option>
              {jobs.map((j) => (
                <option key={j.job_id} value={j.job_id}>{j.job_id} · {j.date}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="w-full rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-slate-700 disabled:opacity-50"
            data-testid={`${idPrefix}-save`}
          >
            {saving ? "Saving…" : "Save adjustment"}
          </button>
        </div>
      )}
    </div>
  );
};
